import { useEffect, useRef, useState } from 'react'
import { useAuthContext } from '@contexts/authContext'
import { colors, size } from '@styles/theme'
import snakecaseKeys from 'snakecase-keys'
import useLoading from '@hooks/useLoading'
import axios from '@utils/webRequests'
import { SectionHeader, SectionWrapper } from './AccountElements'
import { PiAtBold, PiCheckCircleBold } from 'react-icons/pi'
import { Button, InputAdornment } from '@mui/material'
import { FormTextField } from '@elements/Inputs'
import useTrigger from '@hooks/useTrigger'

const SettingsTextField = ({ ...rest }) => (
  <FormTextField
    labelProps={{
      style: {
        width: '100%',
        paddingTop: `${size(1)}`
      }
    }}
    {...rest}
  />
)

const SingleInputForm = ({ onSubmit, value, handleChange, isLoading, setDefaultOnMount, ...rest }) => {
  const [didSave, setDidSave] = useState(false)
  const defaultValue = useRef(value)

  useEffect(() => {
    if (setDefaultOnMount) {
      setDefaultOnMount(defaultValue)
    }
  }, [])

  const innerHandleSubmit = (e) => {
    e.preventDefault()
    if (isLoading || didSave) return;
    onSubmit(e)
      ?.then((message) => {
        if (message !== 'success') return;
        setDidSave(true)
        setTimeout(() => setDidSave(false), 2000)
        defaultValue.current = value
      })
  }

  const innerHandleChange = (e) => {
    if (isLoading) return;
    handleChange(e)
  }

  return (
    <form
      onSubmit={innerHandleSubmit}
      style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      <SettingsTextField
        handleChange={innerHandleChange}
        value={value}
        {...rest}
      />
      {(value !== defaultValue.current || didSave) &&
        <Button
          type='submit'
          variant='contained'
          style={{ borderRadius: '100px', marginLeft: size(1), marginTop: size(3.75), backgroundColor: colors.success(300), color: 'black', flexShrink: 0 }}>
          {didSave ? <><PiCheckCircleBold />&nbsp;Saved</> : (isLoading ? 'Saving...' : 'Save')}
        </Button>
      }
    </form>
  )
}

const EmailForm = () => {
  const [email, setEmail] = useState('')
  const { isLoading, setIsLoading } = useLoading(false)
  const [formErrors, setFormErrors] = useState({})

  const setEmailOnMount = (defaultValue) => {
    axios.get('/api/auth/accounts')
      .then((res) => {
        setEmail(res?.data?.email)
        defaultValue.current = res?.data?.email
      })
      .catch(console.error)
  }
  
  const handleSubmit = (e) => {
    e.preventDefault()
    setIsLoading(true)
    const data = snakecaseKeys({ email })
    return axios.put('/api/auth/accounts', data)
      .then((res) => {
        setFormErrors({})
        setIsLoading(false)
        return Promise.resolve('success')
      })
      .catch((error) => {
        setFormErrors(error?.response?.data?.errors || {})
        setIsLoading(false)
      })
  }

  return (
    <SingleInputForm
      name='email'
      label='Email'
      type='email'
      placeholder='Your email address'
      onSubmit={handleSubmit}
      isLoading={isLoading}
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      error={formErrors?.email}
      helperText={formErrors?.email}
      setDefaultOnMount={setEmailOnMount}
    />
  )
}

const DisplayNameForm = () => {
  const { state: authState, handleInstantUpdate } = useAuthContext()
  const [displayName, setDisplayName] = useState(authState?.user?.displayName || '')
  const { isLoading, setIsLoading } = useLoading(false)
  const [formErrors, setFormErrors] = useState({})
  
  const handleSubmit = (e) => {
    e.preventDefault()
    setIsLoading(true)
    const data = snakecaseKeys({ displayName })
    return axios.put('/api/auth/accounts', data)
      .then((res) => {
        setFormErrors({})
        setIsLoading(false)
        handleInstantUpdate({ ...authState.user, displayName: data.display_name })
        return Promise.resolve('success')
      })
      .catch((error) => {
        setFormErrors(error?.response?.data?.errors || {})
        setIsLoading(false)
      })
  }

  return (
    <SingleInputForm
      onSubmit={handleSubmit}
      isLoading={isLoading}
      name='displayName'
      label='Display Name'
      placeholder='Your display name'
      value={displayName}
      onChange={(e) => setDisplayName(e.target.value)}
      error={formErrors?.displayName}
      helperText={formErrors?.displayName}
    />
  )
}

const UseHandleForm = () => {
  const { state: authState, handleInstantUpdate } = useAuthContext()
  const [handle, sethandle] = useState(authState?.user?.handle || '')
  const { isLoading, setIsLoading } = useLoading(false)
  const [formErrors, setFormErrors] = useState({})
  const [isHandleValid, setIsHandleValid] = useState(false)
  
  useTrigger(handle, 500, () => {
    if (handle && handle !== authState?.user?.handle) {
      validateUserHandle()
    } else {
      setIsHandleValid(false)
    }
  })

  const validateUserHandle = () => {
    axios.post(`/api/auth/account_validations`, { handle })
      .then((res) => {
        if (!res?.data?.handle) {
          setIsHandleValid(true)
          setFormErrors({})
        } else {
          setIsHandleValid(false)
          setFormErrors({ handle: res.data.handle })
        }
    })
    .catch(console.log)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setIsLoading(true)
    const data = snakecaseKeys({ handle })
    return axios.put('/api/auth/accounts', data)
      .then((res) => {
        setFormErrors({})
        setIsLoading(false)
        handleInstantUpdate({ ...authState.user, handle: data.handle })
        setIsHandleValid(false)
        return Promise.resolve('success')
      })
      .catch((error) => {
        setFormErrors(error?.response?.data?.errors || {})
        setIsLoading(false)
      })
  }

  const renderHandleIsValid = () => (
    <span style={{ color: colors.success(500) }}>user handle available</span>
  )

  return (
    <SingleInputForm
      onSubmit={handleSubmit}
      isLoading={isLoading}
      name='handle'
      label='User Handle'
      placeholder='Your user handle'
      value={handle}
      onChange={(e) => sethandle(e.target.value)}
      error={formErrors?.handle}
      helperText={formErrors?.handle || (isHandleValid && renderHandleIsValid())}
      slotProps={{ input: {
        startAdornment: (
          <InputAdornment position="start">
            <PiAtBold fill='white' />
          </InputAdornment>
        )
      }}}
    />
  )
}

const AccountUserInformationSection = () => {
  return (
    <SectionWrapper>
      <SectionHeader>
        User Information
      </SectionHeader>
      <EmailForm />
      <DisplayNameForm />
      <UseHandleForm />
    </SectionWrapper>
  )
}

export default AccountUserInformationSection
